import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { captureException } from '../../lib/sentry';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    // No-op when Sentry isn't initialised (offline dev / no DSN).
    captureException(error, { componentStack: errorInfo.componentStack });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 text-center">
          <div className="bg-red-50 border border-red-200 p-8 rounded-xl max-w-lg shadow-sm">
            <h2 className="text-red-700 font-headline font-bold text-2xl mb-4">
              An unexpected error occurred
            </h2>
            <p className="text-red-600/80 mb-6 font-body text-sm">
              The application encountered a critical problem and could not continue.
            </p>
            <div className="bg-white p-4 rounded text-left overflow-auto text-xs text-red-900 border border-red-100 font-mono mb-6 max-h-40">
              {this.state.error?.message}
            </div>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
